/**
 * Letter Shuffle — Microgame Page
 *
 * Timed word-forming game. Player receives scrambled letter tiles and
 * must form as many valid words as possible before time runs out.
 *
 * Features:
 * - 3 difficulty levels (Easy 7 letters, Medium 9, Hard 12)
 * - Click-to-compose tiles + physical keyboard input
 * - Countdown timer with color transitions (green → yellow → red)
 * - Optimistic UI with local pre-validation
 * - Shake animation for client-detectable errors (no toasts)
 * - Toast only for server-validated "not in dictionary"
 * - Hero celebration for 7+ letter words
 * - End-of-round overlay with best word, longest possible reveal
 * - Shuffle button to re-randomize tile display order
 */

import { logger } from '../lib/logger';
import { getAnimationPref, type AnimationTier } from '../lib/animation-pref';
import { showInfo, showError } from '../lib/toast';
import { balanceService } from '../services/balance-service';
import { triggerCelebration } from '../stores/celebration-store';
import { safeInitCelebrations } from '../utils/celebration-handler';
import { showEndOfGameOverlay, buildSoloWinConfig, type NextGameSuggestions } from '../components/end-of-game-overlay';
import { runSequence, type SequenceHandle } from '../lib/animation-orchestrator';
import { fireAndForgetCelebration } from '../lib/animation-effects';
import { initAnimationsModal } from '../components/animation-tier-modal';
import { fireGuestEvent, fireGameCompleted, hookGuestCompletionToEOG } from '../lib/guest-conversion-engine';
import { isGuestUser } from '../lib/auth-state';
import { authModal, ensureAuthModalExists } from '../components/auth-modal';
import { UserAPI } from '../utils/user-api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FoundWord {
  word: string;
  score: number;
  time_ms: number;
}

interface RoundState {
  round_id: string;
  letters: string[];
  difficulty: string;
  min_word_length: number;
  expires_at: string;
  found_words: FoundWord[];
  total_score: number;
  status: string;
  summary?: RoundSummary;
  secret_word_hint?: string;
  total_possible_words?: number;
}

interface SubmitResponse {
  valid: boolean;
  word: string;
  word_score?: number;
  total_score: number;
  found_words_count?: number;
  status: string;
  reason?: string;
  is_first_word?: boolean;
  used_all_letters?: boolean;
  fp_awarded?: number;
  summary?: RoundSummary;
  is_secret_word?: boolean;
  has_rare_letter?: boolean;
  combo_level?: number;
  combo_bonus?: number;
  play_event_id?: string;
  next_game_suggestions?: NextGameSuggestions;
}

interface RoundSummary {
  total_words: number;
  total_score: number;
  best_word: string;
  best_word_score?: number;
  longest_possible: string | null;
  fp_awarded: number;
  first_word_bonus: boolean;
  end_reason: string;
  difficulty: string;
  total_possible_words?: number;
  secret_word?: string;
  secret_word_found?: boolean;
  missed_words?: Array<{ word: string; score: number }>;
  performance_tier?: string;
  performance_ratio?: number;
  near_miss_message?: string | null;
}

interface StatsResponse {
  personal_best: number;
  total_words: number;
  total_rounds: number;
  average_score: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = '/api/games/letter-shuffle';
const HERO_WORD_LENGTH = 6;
const TIMER_WARNING_SECONDS = 30;
const TIMER_DANGER_SECONDS = 10;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let currentRound: RoundState | null = null;
let displayLetters: string[] = []; // Current display order (shuffleable)
let composedLetters: Array<{ letter: string; tileIndex: number }> = [];
let isProcessing = false;
let gameActive = false;
let isGuest = false;
let timerInterval: ReturnType<typeof setInterval> | null = null;
let previousBestScore = 0;
let pendingEntryContext: string | undefined;
let eogHandle: SequenceHandle | undefined;

// Per-submission bonus tracking (for rendering in found words list)
let lastSubmitBonuses = {
  is_secret_word: false,
  has_rare_letter: false,
  combo_level: 0,
  combo_bonus: 0,
};

// Animation level
let animationLevel: AnimationTier = 'playful';

// Cached DOM elements
let selectionSection: HTMLElement;
let gameArea: HTMLElement;
let tilesContainer: HTMLElement;
let compositionRow: HTMLElement;
let timerDisplay: HTMLElement;
let scoreDisplay: HTMLElement;
let foundWordsList: HTMLElement;
let foundWordsLabel: HTMLElement;
let gameInfoLabel: HTMLElement;
let submitWordBtn: HTMLButtonElement;
let backspaceBtn: HTMLButtonElement;
let shuffleBtn: HTMLButtonElement;
let endRoundBtn: HTMLButtonElement;
let secretWordHint: HTMLElement;
let comboBadge: HTMLElement;

// ---------------------------------------------------------------------------
// Animation Tier (shared component)
// ---------------------------------------------------------------------------

function loadAnimationLevel(): void {
  animationLevel = getAnimationPref();
}

function initLetterShuffleAnimationsModal(): void {
  initAnimationsModal({
    gamePrefix: 'ls',
    cssClassPrefix: 'ls-anim',
    pageElementId: 'letterShufflePage',
    onTierChange: (tier) => {
      animationLevel = tier as AnimationTier;
    },
  });
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

async function init(): Promise<void> {
  logger.info('[LetterShuffle] Initializing');

  selectionSection = document.getElementById('selectionSection')!;
  gameArea = document.getElementById('gameArea')!;
  tilesContainer = document.getElementById('tilesContainer')!;
  compositionRow = document.getElementById('compositionRow')!;
  timerDisplay = document.getElementById('timerDisplay')!;
  scoreDisplay = document.getElementById('scoreDisplay')!;
  foundWordsList = document.getElementById('foundWordsList')!;
  foundWordsLabel = document.getElementById('foundWordsLabel')!;
  gameInfoLabel = document.getElementById('gameInfoLabel')!;
  submitWordBtn = document.getElementById('submitWordBtn') as HTMLButtonElement;
  backspaceBtn = document.getElementById('backspaceBtn') as HTMLButtonElement;
  shuffleBtn = document.getElementById('shuffleBtn') as HTMLButtonElement;
  endRoundBtn = document.getElementById('endRoundBtn') as HTMLButtonElement;
  secretWordHint = document.getElementById('secretWordHint')!;
  comboBadge = document.getElementById('comboBadge')!;

  if (!selectionSection || !gameArea) {
    logger.error('[LetterShuffle] Missing required DOM elements');
    return;
  }

  // Bootstrap tooltip on info button
  const infoBtn = document.getElementById('infoBtn');
  if (infoBtn && typeof bootstrap !== 'undefined') {
    new bootstrap.Tooltip(infoBtn);
  }

  // Event delegation on difficulty cards
  const difficultyCards = document.getElementById('difficultyCards');
  if (difficultyCards) {
    difficultyCards.addEventListener('click', handleDifficultyClick);
  }

  // Event delegation on tiles
  tilesContainer.addEventListener('click', handleTileClick);

  // Buttons
  submitWordBtn.addEventListener('click', handleSubmitWord);
  backspaceBtn.addEventListener('click', handleBackspace);
  shuffleBtn.addEventListener('click', handleShuffle);
  endRoundBtn.addEventListener('click', handleEndRound);

  // Physical keyboard
  document.addEventListener('keydown', handleKeydown);

  // Animation modal + tier
  loadAnimationLevel();
  initLetterShuffleAnimationsModal();

  // Guest status (must resolve before loadStats to show placeholder)
  loadGuestStatus().then(() => loadStats());

  // Init celebrations
  safeInitCelebrations();

  // Read URL params for guided play flow
  const urlParams = new URLSearchParams(window.location.search);
  const ecParam = urlParams.get('ec');
  const difficultyParam = urlParams.get('difficulty');
  if (ecParam) {
    pendingEntryContext = ecParam;
  }
  if (urlParams.has('ec') || urlParams.has('difficulty')) {
    history.replaceState({}, '', window.location.pathname);
  }

  // Auto-start if difficulty was provided via URL
  if (difficultyParam && difficultyParam !== 'auto') {
    void startGame(difficultyParam);
  } else if (difficultyParam === 'auto') {
    // 'auto' → try saved preference, fall back to difficulty selection screen
    const saved = await UserAPI.getPreference('letter_shuffle_difficulty').catch(() => null);
    if (typeof saved === 'string' && saved) {
      void startGame(saved);
    }
  }
  // No param or no saved pref → show difficulty selection screen

  logger.info('[LetterShuffle] Ready');
}

// ---------------------------------------------------------------------------
// API Calls
// ---------------------------------------------------------------------------

async function loadGuestStatus(): Promise<void> {
  try {
    const resp = await fetch('/me', { credentials: 'same-origin' });
    if (resp.ok) {
      const data = await resp.json();
      isGuest = isGuestUser(data);
    } else {
      isGuest = true;
    }
  } catch {
    isGuest = true;
  }
  logger.info('[LetterShuffle] Guest status:', isGuest);
}

async function startGame(difficulty: string): Promise<void> {
  if (isProcessing) return;
  isProcessing = true;

  // Cancel any pending end-of-game sequence
  eogHandle?.cancel();
  eogHandle = undefined;

  // Consume pending entry context (one-shot)
  const ec = pendingEntryContext;
  pendingEntryContext = undefined;

  try {
    const resp = await fetch(`${API_BASE}/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ difficulty, ...(ec ? { entry_context: ec } : {}) }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${resp.status}`);
    }

    const data: RoundState = await resp.json();
    currentRound = data;
    displayLetters = [...data.letters];
    composedLetters = [];
    gameActive = true;
    window.__versionGuard?.setSafeToReload(false);

    showGameArea(difficulty);
    renderTiles();
    renderComposition();
    renderFoundWords();
    updateScore(0);
    startTimer();
    loadStats();

    // Save difficulty preference (authenticated users only)
    if (!isGuest) {
      UserAPI.setPreference('letter_shuffle_difficulty', difficulty).catch(() => {});
    }

    // Show secret word hint
    if (data.secret_word_hint && secretWordHint) {
      secretWordHint.textContent = data.secret_word_hint;
      secretWordHint.style.display = '';
    } else if (secretWordHint) {
      secretWordHint.style.display = 'none';
    }

    // Hide end round button initially (show after first word)
    endRoundBtn.style.display = 'none';

    logger.info(`[LetterShuffle] Round started: ${data.round_id}, letters: ${data.letters.join('')}`);
  } catch (err) {
    logger.error('[LetterShuffle] Failed to start game', err);
    showError('Could not start game — try again.');
  } finally {
    isProcessing = false;
  }
}

async function submitWord(word: string): Promise<void> {
  if (!gameActive || isProcessing || !currentRound) return;
  isProcessing = true;

  try {
    const resp = await fetch(`${API_BASE}/submit-word`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ round_id: currentRound.round_id, word }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${resp.status}`);
    }

    const data: SubmitResponse = await resp.json();

    if (data.valid) {
      // Update state
      const foundWord: FoundWord = {
        word: data.word,
        score: data.word_score || 0,
        time_ms: 0,
      };
      currentRound.found_words.push(foundWord);
      currentRound.total_score = data.total_score;

      // Track bonus info for rendering
      lastSubmitBonuses = {
        is_secret_word: data.is_secret_word || false,
        has_rare_letter: data.has_rare_letter || false,
        combo_level: data.combo_level || 0,
        combo_bonus: data.combo_bonus || 0,
      };

      // Clear composition
      composedLetters = [];
      renderComposition();
      renderTiles();
      renderFoundWords();
      updateScore(data.total_score);

      // Show end round button after first word
      endRoundBtn.style.display = '';

      // Hero celebration for long words
      if (word.length >= HERO_WORD_LENGTH) {
        triggerCelebration('bonus');
      }

      // Secret word discovery celebration
      if (data.is_secret_word) {
        triggerCelebration('bonus');
      }

      // Combo feedback badge
      if (data.combo_level && data.combo_level > 0 && data.combo_bonus) {
        showComboBadge(data.combo_level, data.combo_bonus);
      }

      // First word bonus feedback — shown as part of the word item in list
      if (data.is_first_word) {
        logger.info('[LetterShuffle] First word bonus awarded');
      }

    } else {
      // Server said invalid — only "not_in_dictionary" gets a toast
      if (data.reason === 'not_in_dictionary') {
        showInfo('Not a valid word');
      }

      // If round expired during submission
      if (data.status === 'finished' && data.summary) {
        gameActive = false;
        stopTimer();
        showRoundEndOverlay(data.summary, data.play_event_id, data.next_game_suggestions);
        return;
      }

      // Shake the composition row for server rejections too
      shakeComposition();
    }
  } catch (err) {
    logger.error('[LetterShuffle] Submit failed', err);
    showError('Submit failed — try again.');
  } finally {
    isProcessing = false;
  }
}

async function endRound(): Promise<void> {
  if (!gameActive || isProcessing || !currentRound) return;
  isProcessing = true;

  try {
    const resp = await fetch(`${API_BASE}/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ round_id: currentRound.round_id }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${resp.status}`);
    }

    const data = await resp.json();
    gameActive = false;
    stopTimer();

    if (data.summary) {
      showRoundEndOverlay(data.summary, data.play_event_id, data.next_game_suggestions);
    }
  } catch (err) {
    logger.error('[LetterShuffle] End round failed', err);
    showError('Failed to end round — try again.');
  } finally {
    isProcessing = false;
  }
}

async function loadStats(): Promise<void> {
  // Show guest placeholder if not authenticated
  if (isGuest) {
    for (const cardId of ['selectionStatsCard', 'statsCard']) {
      const card = document.getElementById(cardId);
      if (!card) continue;
      const cardBody = card.querySelector('.card-body');
      if (cardBody) {
        cardBody.innerHTML = `
          <h6 class="text-muted mb-3">Your Stats</h6>
          <div class="text-center py-3" data-testid="guest-stats-placeholder">
            <p class="mb-1 text-muted">Your game stats will appear here</p>
            <p class="small text-muted mb-2">Available when signed in</p>
            <a href="#" class="btn btn-outline-primary btn-sm" data-testid="guest-stats-signin">
              Sign in to start tracking →
            </a>
          </div>`;
        const signinLink = cardBody.querySelector('[data-testid="guest-stats-signin"]');
        if (signinLink) {
          signinLink.addEventListener('click', (e) => {
            e.preventDefault();
            void fireGuestEvent('tried_locked_feature', true).then((shown) => {
              if (!shown) {
                ensureAuthModalExists();
                authModal.show();
              }
            });
          });
        }
      }
    }
    return;
  }

  try {
    const resp = await fetch(`${API_BASE}/stats`);
    if (!resp.ok) return;

    const data: StatsResponse = await resp.json();
    previousBestScore = data.personal_best;

    const bestVal = data.personal_best > 0 ? String(data.personal_best) : '-';
    const wordsVal = data.total_words > 0 ? String(data.total_words) : '-';
    const roundsVal = data.total_rounds > 0 ? String(data.total_rounds) : '-';

    // Update both in-game and selection stats cards
    for (const prefix of ['stat', 'selStat']) {
      const bestEl = document.getElementById(prefix === 'stat' ? 'statBest' : 'selStatBest');
      const wordsEl = document.getElementById(prefix === 'stat' ? 'statWords' : 'selStatWords');
      const roundsEl = document.getElementById(prefix === 'stat' ? 'statRounds' : 'selStatRounds');
      if (bestEl) bestEl.textContent = bestVal;
      if (wordsEl) wordsEl.textContent = wordsVal;
      if (roundsEl) roundsEl.textContent = roundsVal;
    }
  } catch (err) {
    logger.warn('[LetterShuffle] Failed to load stats', err);
  }
}

// ---------------------------------------------------------------------------
// Event Handlers
// ---------------------------------------------------------------------------

function handleDifficultyClick(e: Event): void {
  const target = (e.target as HTMLElement).closest('[data-difficulty]') as HTMLElement | null;
  if (!target) return;

  const difficulty = target.dataset.difficulty;
  if (!difficulty) return;

  startGame(difficulty);
}

function handleTileClick(e: Event): void {
  if (!gameActive) return;
  const target = (e.target as HTMLElement).closest('.ls-tile') as HTMLElement | null;
  if (!target || target.classList.contains('ls-tile-used')) return;

  const index = parseInt(target.dataset.tileIndex || '-1', 10);
  if (index < 0) return;

  const letter = displayLetters[index];
  composedLetters.push({ letter, tileIndex: index });
  renderTiles();
  renderComposition();
}

function handleBackspace(): void {
  if (composedLetters.length === 0) return;
  composedLetters.pop();
  renderTiles();
  renderComposition();
}

function handleShuffle(): void {
  if (!gameActive || !currentRound) return;

  // Fisher-Yates shuffle of display order
  const arr = [...displayLetters];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  displayLetters = arr;

  // Reset composition (tile indices are invalidated)
  composedLetters = [];
  renderTiles();
  renderComposition();
}

function handleEndRound(): void {
  endRound();
}

function handleSubmitWord(): void {
  attemptSubmit();
}

function handleKeydown(e: KeyboardEvent): void {
  if (!gameActive) return;

  // Ignore if typing in input/textarea
  const tag = (e.target as HTMLElement).tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

  const key = e.key;

  if (key === 'Enter') {
    e.preventDefault();
    attemptSubmit();
    return;
  }

  if (key === 'Backspace') {
    e.preventDefault();
    handleBackspace();
    return;
  }

  // Letter input
  if (key.length === 1 && /^[a-zA-Z]$/.test(key)) {
    e.preventDefault();
    const letter = key.toUpperCase();
    addLetterByKeyboard(letter);
  }
}

function addLetterByKeyboard(letter: string): void {
  // Find first available (unused) tile with this letter
  const usedIndices = new Set(composedLetters.map(c => c.tileIndex));

  for (let i = 0; i < displayLetters.length; i++) {
    if (displayLetters[i] === letter && !usedIndices.has(i)) {
      composedLetters.push({ letter, tileIndex: i });
      renderTiles();
      renderComposition();
      return;
    }
  }
  // Letter not available — subtle shake
  shakeComposition();
}

// ---------------------------------------------------------------------------
// Pre-validation + Submit
// ---------------------------------------------------------------------------

function attemptSubmit(): void {
  if (!gameActive || !currentRound || composedLetters.length === 0) return;

  const word = composedLetters.map(c => c.letter).join('');
  const minLen = currentRound.min_word_length;

  // Local pre-validation: too short
  if (word.length < minLen) {
    shakeComposition();
    return;
  }

  // Local pre-validation: duplicate
  const foundSet = new Set(currentRound.found_words.map(fw => fw.word));
  if (foundSet.has(word)) {
    // Highlight the duplicate in the list
    highlightDuplicate(word);
    return;
  }

  // Letters available is implicitly guaranteed by tile selection.
  // For keyboard input, addLetterByKeyboard already validates availability.
  // But double-check anyway:
  if (!canFormFromLetters(word, currentRound.letters)) {
    shakeComposition();
    return;
  }

  // Pass local validation — submit to server (for dictionary check)
  submitWord(word);
}

function canFormFromLetters(word: string, letters: string[]): boolean {
  const available: Record<string, number> = {};
  for (const l of letters) {
    available[l] = (available[l] || 0) + 1;
  }
  for (const ch of word) {
    if (!available[ch] || available[ch] <= 0) return false;
    available[ch]--;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function renderTiles(): void {
  const usedIndices = new Set(composedLetters.map(c => c.tileIndex));
  const isHard = currentRound?.difficulty === 'hard';

  // Compute grid columns: always produce 2 even rows regardless of tile count
  const cols = Math.ceil(displayLetters.length / 2);
  tilesContainer.style.setProperty('--ls-tile-cols', String(cols));

  tilesContainer.innerHTML = displayLetters.map((letter, i) => {
    const used = usedIndices.has(i);
    const usedClass = used ? 'ls-tile-used' : '';
    return `<button class="ls-tile ${usedClass}" data-tile-index="${i}" data-testid="tile-${i}" ${used ? 'disabled' : ''}>${letter}</button>`;
  }).join('');
}

function renderComposition(): void {
  if (composedLetters.length === 0) {
    compositionRow.innerHTML = '';
    toggleSubmitButton();
    return;
  }

  compositionRow.innerHTML = composedLetters.map(c =>
    `<span class="ls-composition-letter">${c.letter}</span>`
  ).join('');
  toggleSubmitButton();
}

function toggleSubmitButton(): void {
  if (!submitWordBtn || !currentRound) return;
  const minLen = currentRound.min_word_length;
  const shouldDisable = composedLetters.length < minLen;
  submitWordBtn.disabled = shouldDisable;
  submitWordBtn.classList.toggle('ls-submit-disabled', shouldDisable);
}

function renderFoundWords(): void {
  if (!currentRound) return;

  const words = currentRound.found_words;
  if (words.length === 0) {
    foundWordsList.innerHTML = '<div class="ls-found-words-empty">Find your first word!</div>';
    foundWordsLabel.textContent = 'Words Found';
    return;
  }

  foundWordsLabel.textContent = `Words Found (${words.length})`;

  // Newest at top
  const reversed = [...words].reverse();
  foundWordsList.innerHTML = reversed.map((fw, i) => {
    const isNew = i === 0 && animationLevel !== 'none';
    const isNewestWord = i === 0;
    const wordClass = isNew ? 'ls-found-word-item new-word' : 'ls-found-word-item';
    // Mark first-word bonus (it's always the last item in the original array, i.e., index 0)
    const isFirstWord = words.length > 0 && fw === words[0] && words.length === reversed.length;
    const bonus = isFirstWord && words.length >= 1 ? '<span class="ls-found-word-bonus">+5 bonus</span>' : '';

    // Secret word badge (only for the most recently submitted word)
    const secretBadge = isNewestWord && lastSubmitBonuses.is_secret_word
      ? '<span class="ls-secret-word-badge" data-testid="secret-word-badge">Secret Word! +40</span>'
      : '';

    // Rare letter badge (only for the most recently submitted word)
    const rareBadge = isNewestWord && lastSubmitBonuses.has_rare_letter
      ? '<span class="ls-rare-letter-badge" data-testid="rare-letter-badge">Rare letter +5</span>'
      : '';

    // Gold highlight for secret word discovery
    const secretClass = isNewestWord && lastSubmitBonuses.is_secret_word ? ' ls-secret-word-found' : '';

    return `<div class="${wordClass}${secretClass}" data-word="${fw.word}" data-testid="found-word-${fw.word}">
      <span class="ls-found-word-text">${fw.word}</span>
      <span class="ls-found-word-score">${fw.score} pts${bonus}${secretBadge}${rareBadge}</span>
    </div>`;
  }).join('');
}

function updateScore(score: number): void {
  scoreDisplay.textContent = String(score);

  // Brief bump animation
  if (animationLevel !== 'none') {
    scoreDisplay.classList.add('score-bump');
    setTimeout(() => scoreDisplay.classList.remove('score-bump'), 200);
  }
}

function shakeComposition(): void {
  if (animationLevel === 'none') return;
  compositionRow.classList.remove('shake');
  // Force reflow
  void compositionRow.offsetWidth;
  compositionRow.classList.add('shake');
}

function highlightDuplicate(word: string): void {
  const item = foundWordsList.querySelector(`[data-word="${word}"]`) as HTMLElement | null;
  if (item) {
    item.classList.add('highlight-duplicate');
    setTimeout(() => item.classList.remove('highlight-duplicate'), 1000);
  }
}

function showComboBadge(level: number, bonus: number): void {
  if (!comboBadge) return;
  const fireIcon = animationLevel === 'playful' ? ' 🔥' : '';
  comboBadge.textContent = `Combo x${level}${fireIcon} +${bonus}`;
  comboBadge.classList.remove('ls-combo-show');
  // Force reflow for re-triggering animation
  void comboBadge.offsetWidth;
  comboBadge.classList.add('ls-combo-show');

  setTimeout(() => {
    comboBadge.classList.remove('ls-combo-show');
  }, 1500);
}

// ---------------------------------------------------------------------------
// Timer
// ---------------------------------------------------------------------------

function startTimer(): void {
  stopTimer();
  updateTimerDisplay();
  timerInterval = setInterval(updateTimerDisplay, 1000);
}

function stopTimer(): void {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
}

function updateTimerDisplay(): void {
  if (!currentRound) return;

  const expiresAt = new Date(currentRound.expires_at).getTime();
  const now = Date.now();
  const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  timerDisplay.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  // Color transitions
  timerDisplay.classList.remove('timer-warning', 'timer-danger', 'timer-pulse');
  if (remaining <= TIMER_DANGER_SECONDS) {
    timerDisplay.classList.add('timer-danger', 'timer-pulse');
  } else if (remaining <= TIMER_WARNING_SECONDS) {
    timerDisplay.classList.add('timer-warning', 'timer-pulse');
  }

  // Auto-end when timer expires
  if (remaining <= 0 && gameActive) {
    stopTimer();
    timerDisplay.textContent = '0:00';

    // End round on server — don't set gameActive=false here;
    // endRound() will handle it after the server responds.
    // If a word submission is in progress, retry after a short delay.
    if (isProcessing) {
      const retryTimer = setInterval(() => {
        if (!isProcessing) {
          clearInterval(retryTimer);
          endRound();
        }
      }, 200);
    } else {
      endRound();
    }
  }
}

// ---------------------------------------------------------------------------
// Round End
// ---------------------------------------------------------------------------

function showRoundEndOverlay(summary: RoundSummary, playEventId?: string, nextGameSuggestions?: NextGameSuggestions): void {
  const fp = summary.fp_awarded || 0;
  const isNewBest = summary.total_score > previousBestScore && previousBestScore > 0;
  const endedEarly = summary.end_reason === 'manual';

  // Refresh FP balance
  if (fp > 0 && !isGuest) {
    balanceService.refresh().catch(err => logger.error('FP refresh failed:', err));
  }

  // FP award toast during game only (not at end — overlay handles it)
  // No FP toast here per design: "correct but only during game, never at end"

  // Performance tier info
  const tierEmojis: Record<string, string> = {
    'Explorer': '🔍',
    'Word Finder': '🧩',
    'Linguist': '📚',
    'Word Master': '🏆',
  };
  const tierSubcopy: Record<string, string> = {
    'Explorer': 'Keep exploring — there\'s always more!',
    'Word Finder': 'Great pattern recognition!',
    'Linguist': 'Impressive vocabulary!',
    'Word Master': 'Incredible — you\'re a word wizard!',
  };

  // Build subtitle parts (v2 layout)
  const subtitleParts: string[] = [];

  if (summary.total_words > 0) {
    // Result message
    subtitleParts.push(`You found ${summary.total_words} word${summary.total_words !== 1 ? 's' : ''} for ${summary.total_score} points!`);

    // Best word
    if (summary.best_word) {
      subtitleParts.push(`Best word: ${summary.best_word} (${summary.best_word_score || summary.best_word.length ** 2} pts) 🌟`);
    }

    // Secret word reveal
    if (summary.secret_word) {
      if (summary.secret_word_found) {
        subtitleParts.push(`Secret Word: ${summary.secret_word} ✅ (+40 bonus!)`);
      } else {
        subtitleParts.push(`Secret Word: ${summary.secret_word} — so close!`);
      }
    }

    // Words found ratio
    if (summary.total_possible_words && summary.total_possible_words > 0) {
      subtitleParts.push(`Words found: ${summary.total_words} / ${summary.total_possible_words} possible`);
    }

    // Missed opportunities (max 3)
    if (summary.missed_words && summary.missed_words.length > 0) {
      const missedLines = summary.missed_words.map(mw => `${mw.word} (${mw.score} pts)`);
      subtitleParts.push(`You missed:\n${missedLines.join('\n')}`);
    }

    // Performance tier
    const tier = summary.performance_tier;
    if (tier) {
      const emoji = tierEmojis[tier] || '';
      const subcopy = tierSubcopy[tier] || '';
      subtitleParts.push(`${tier} ${emoji}\n${subcopy}`);
    }

    // Near-miss message
    if (summary.near_miss_message) {
      subtitleParts.push(summary.near_miss_message);
    }

    if (isNewBest) {
      subtitleParts.push('New personal best! 🏆');
    }
  } else {
    // No words found — encouraging message
    subtitleParts.push('No words this time — the letters can be tricky!');
    if (summary.secret_word) {
      subtitleParts.push(`The secret word was: ${summary.secret_word}`);
    } else if (summary.longest_possible) {
      subtitleParts.push(`The longest possible word was: ${summary.longest_possible}`);
    }
    subtitleParts.push('Give it another go — you\'ll get the hang of it! 💪');
  }

  // Determine message based on outcome
  let message: string;
  if (endedEarly) {
    message = 'Nice work! 🎉';
  } else if (summary.total_words > 0) {
    message = "Time's up! ⏱️";
  } else {
    message = "Time's up! ⏱️";
  }
  const subtitle = subtitleParts.join('\n');

  const ngs = nextGameSuggestions;

  const config = buildSoloWinConfig({
    game: 'letter-shuffle',
    totalScore: summary.total_score ?? 0,
    fpEarned: fp,
    isGuest,
    onPlayAgain: () => {
      void fireGameCompleted(isGuest);
      showSelection();
    },
    playAgainUrl: '/games/letter-shuffle',
    playEventId,
    nextGameSuggestions: ngs,
  });

  // Letter-shuffle-specific overrides
  config.message = message;
  config.subtitle = subtitle;
  config.icon = 'bi-shuffle';

  // Orchestrated end-of-round: optional celebration → overlay
  eogHandle?.cancel();

  const steps = [];
  if (summary.total_score >= 50) {
    steps.push(fireAndForgetCelebration('bonus'));
  }

  eogHandle = runSequence('letter-shuffle-eog', steps, () => {
    showEndOfGameOverlay(config);
    hookGuestCompletionToEOG(isGuest);
  });
  loadStats();
}

// ---------------------------------------------------------------------------
// View Management
// ---------------------------------------------------------------------------

function showGameArea(difficulty: string): void {
  selectionSection.style.display = 'none';
  gameArea.style.display = '';

  const labels: Record<string, string> = {
    easy: 'Easy — 7 Letters',
    medium: 'Medium — 9 Letters',
    hard: 'Hard — 12 Letters',
  };
  gameInfoLabel.textContent = labels[difficulty] || difficulty;
}

function showSelection(): void {
  window.__versionGuard?.setSafeToReload(true);

  // Cancel any pending end-of-game sequence
  eogHandle?.cancel();
  eogHandle = undefined;

  gameArea.style.display = 'none';
  selectionSection.style.display = '';
  gameActive = false;
  currentRound = null;
  composedLetters = [];
  displayLetters = [];
  lastSubmitBonuses = { is_secret_word: false, has_rare_letter: false, combo_level: 0, combo_bonus: 0 };
  stopTimer();
  if (secretWordHint) secretWordHint.style.display = 'none';
  if (comboBadge) comboBadge.classList.remove('ls-combo-show');
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', init);
